import styled, { css } from 'styled-components';

import AppSearchInput from '../../../components/Input/SearchInput';
import { ThemeProp } from '../../../theme/Themes';
import { BOTTOMSHEET_MAX_TOP_MARGIN } from '../../BottomSheet/BottomSheet';
import { CheckboxGrouped } from '../../Checkbox';
import { ShimmerText } from '../../Shimmer';

const commonFontProps = (theme: ThemeProp['theme']) => {
  return `
    font-weight: ${theme.fontWeight.regular};
    font-size: ${theme.fontSize.s};
    line-height: 1.1375rem;
  `;
};

const commonMobileFontProps = (theme: ThemeProp['theme']) => {
  return `
    font-size: ${theme.fontVariants.desktop.body1.fontSize};
    line-height: ${theme.fontVariants.desktop.body1.lineHeight};
  `;
};

export const ContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`;

interface ContainersProps {
  $noPadding?: boolean;
  $isOverflow?: boolean;
  $shouldHideContent?: boolean;
  $shouldExpandToMaxHeight?: boolean;
  $maxHeight?: string;
}

const BOTTOMSHEET_HEADER_HEIGHT = '6rem';
const BOTTOMSHEET_SEARCHBAR_HEIGHT = '3.5rem';

export const Container = styled.div<ContainersProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  ${(props: ContainersProps) => !props.$noPadding && `padding-top: 0.5rem;`}

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding-top: 0;
    padding: 0 0.5rem 1rem 0.5rem;
    height: ${(props: ContainersProps) =>
      props.$shouldExpandToMaxHeight
        ? `calc(100dvh - ${BOTTOMSHEET_HEADER_HEIGHT} - ${BOTTOMSHEET_SEARCHBAR_HEIGHT} - ${BOTTOMSHEET_MAX_TOP_MARGIN})`
        : 'auto'};
  }

  ${(props: ContainersProps) =>
    props.$shouldHideContent &&
    css`
      display: none;
    `};
`;

export const ScrollableContainer = styled.div<ContainersProps>`
  max-height: ${(props: ContainersProps) => props.$maxHeight};
  margin-right: -1rem;
  overflow-y: auto;
  width: 100%;
  ${(props: ContainersProps) => !props.$noPadding && `padding: 0 0.5rem 0.5rem 0.5rem;`}
  ${(props: ThemeProp & ContainersProps) =>
    props.$isOverflow && `border-bottom: 1px solid ${props.theme.colors.neutrals.c100}; margin-bottom: 0.5rem;`}

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    border-bottom: none;
    ${(props: ContainersProps) => !props.$noPadding && `padding: 0 0.75rem 0.5rem 0.75rem;`}
    margin-bottom: 0;
    max-height: unset;
  }
`;

export const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    gap: 0.5rem;
  }
`;

export const ItemsTitle = styled.div<any>`
  margin-right: auto;
  width: 100%;
  ${(props) => props.addPadding && `padding-left: 0.2rem;`}
  overflow: hidden;
  position: relative;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ theme }: ThemeProp) => commonFontProps(theme)}

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    ${({ theme }: ThemeProp) => commonMobileFontProps(theme)}
  }
`;

export const ItemsCount = styled.span`
  ${({ theme }: ThemeProp) => commonFontProps(theme)}
  margin-left: 0.2rem;
  color: ${({ theme }: ThemeProp) => theme.colors.neutrals.c500};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    ${({ theme }: ThemeProp) => commonMobileFontProps(theme)}
  }
`;

export const ModalContainer = styled.div`
  height: 25rem;
  display: flex;
  flex-direction: column;
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0.5rem;
  margin: 0.625rem 0;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding: 0 0.75rem;
    margin: 0 0 1rem 0;
  }
`;

export const StyledSearchInput = styled(AppSearchInput)`
  width: 100%;
  height: 2rem !important;

  & .nextui-input-main-container {
    height: 2rem !important;
  }

  & input::placeholder {
    color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c800};

    & .nextui-input-main-container {
      height: 2.5rem !important;
    }

    ${({ theme }: ThemeProp) => commonMobileFontProps(theme)}
    & input::placeholder {
      color: ${(props: ThemeProp) => props.theme.colors.neutrals.c800};
      ${({ theme }: ThemeProp) => commonMobileFontProps(theme)}
    }
  }
`;

export const SelectionInputsContainer = styled.div`
  flex: 1;
`;

export const Grid = styled.div`
  display: grid;
  grid-column-gap: 2rem;
  grid-row-gap: 0.625rem;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  grid-auto-rows: 1fr;
  flex: 1;
  word-wrap: break-word;
  padding: 0 0.5rem;
  font-size: ${(props: any) => props.theme.lineHeight.s};
`;

const BASE_CHECKBOX_ICON_SCALE = 0.5;
const BASE_CHECKBOX_SIZE_IN_PX = 20;
const getScaledIconScaleFactor = (nextCheckboxSize: number) => {
  const scalingFactor = nextCheckboxSize / BASE_CHECKBOX_SIZE_IN_PX;
  return BASE_CHECKBOX_ICON_SCALE * scalingFactor;
};

const getScaledCheckboxStyles = (nextCheckboxSizeInPx: number) => {
  return css`
    .nextui-checkbox-container {
      height: ${nextCheckboxSizeInPx}px;
      width: ${nextCheckboxSizeInPx}px;

      .nextui-icon-check {
        transform: rotate(45deg) scale(${getScaledIconScaleFactor(nextCheckboxSizeInPx)});
      }
    }
  `;
};

interface StyledCheckboxGroupedProps {
  $reverseCheckboxOrder?: boolean;
  $checkboxSizeInPx?: number;
  $isSelected?: boolean;
}

export const StyledCheckboxGrouped = styled(CheckboxGrouped)<StyledCheckboxGroupedProps>`
  ${({ $reverseCheckboxOrder }: StyledCheckboxGroupedProps) =>
    $reverseCheckboxOrder ? 'flex-direction: row-reverse' : null};
  ${({ $checkboxSizeInPx }: StyledCheckboxGroupedProps) => getScaledCheckboxStyles($checkboxSizeInPx)};

  .nextui-checkbox-mask {
    pointer-events: auto;
  }

  &.nextui-checkbox-label {
    border-radius: 0.375rem;
    padding: 0 0.5rem;
  }

  .nextui-checkbox-mask:hover {
    &::before {
      background: ${({ theme }: ThemeProp) => theme.colors.layout.surface.default};
      border-color: ${({ theme }: ThemeProp) => theme.colors.neutrals.c200};
    }
  }

  &:hover {
    .nextui-checkbox-mask {
      &::before {
        background: ${({ theme }: ThemeProp) => theme.colors.layout.surface.default};
        border-color: ${({ theme }: ThemeProp) => theme.colors.neutrals.c200};
      }
    }
  }

  ${(props: ThemeProp & StyledCheckboxGroupedProps) =>
    props.$isSelected
      ? css`
          &.nextui-checkbox-label {
            background: ${(props: ThemeProp) => props.theme.colors.main.c050};
          }
          & .nextui-checkbox-text {
            color: ${props.theme.colors.main.c600};
            background: ${(props: ThemeProp) => props.theme.colors.main.c050};
          }
        `
      : null};

  @media not all and (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    &:hover {
      &.nextui-checkbox-label {
        background: ${(props: ThemeProp) => props.theme.colors.main.c050};
      }
    }
  }
`;

export const NoResultsText = styled.p`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  color: ${(props: ThemeProp) => props.theme.colors.content.disabled};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
  font-style: italic;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.lineHeight};
  padding: 0.625rem 0.5rem 0.625rem 0.5rem;
  word-break: break-all;
  letter-spacing: normal;
  justify-content: space-between;
`;

export const EmptyOptionsViewContainer = styled.div`
  margin: 1rem 0.5rem 0.75rem 0.5rem;
  text-align: center;
  align-self: center;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    max-width: 16.5rem;
    margin: 0 0 2.5rem 0;
  }
`;

export const EmptyOptionsTitle = styled.p`
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  text-align: center;
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
  margin-bottom: 0.125rem;
  letter-spacing: normal;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.mlg};
    line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
    margin-bottom: 0.25rem;
  }
`;

export const EmptyOptionsSubTitle = styled.p`
  letter-spacing: normal;
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.fontSize};
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
    line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body1.lineHeight};
  }
`;

export const EmptyOptionsClearButton = styled(EmptyOptionsSubTitle)`
  color: ${(props: ThemeProp) => props.theme.colors.main.c600};
  cursor: pointer;
`;

interface StyledShimmerTitleProps {
  $borderRadius?: string;
}

export const StyledShimmerTitle = styled(ShimmerText)<StyledShimmerTitleProps>`
  && .shimmer {
    ${(props: StyledShimmerTitleProps) =>
      props.$borderRadius
        ? css`
            border-radius: ${props.$borderRadius};
          `
        : null}
  }
`;

export const ShimmerOptionRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0.625rem 0.5rem;
  justify-content: space-between;
  height: 2.3875rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding: 0.5rem;
    height: unset;
  }
`;

export const ShimmerOptionsIconAndLabel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

export const ShimmerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const ShimmerOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    gap: 0.5rem;
  }
`;
